import { useMemo, useState, useRef, useEffect } from "react";

interface LocationDropdownProps {
  label: string;
  value: string;
  options: Array<{ value: string; label: string; code?: string }>;
  onChange: (value: string, code?: string) => void;
  placeholder?: string;
  required?: boolean;
  disabled?: boolean;
}

const LocationDropdown: React.FC<LocationDropdownProps> = ({
  label,
  value,
  options,
  onChange,
  placeholder = "Select",
  required = false,
  disabled = false,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const rootRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!isOpen) return;

    const onDocumentClick = (event: MouseEvent | TouchEvent) => {
      const target = (event.target as Node) || null;
      if (rootRef.current && target && !rootRef.current.contains(target)) {
        setIsOpen(false);
      }
    };

    const onDocumentPointerDown = (event: Event) => {
      onDocumentClick(event as MouseEvent | TouchEvent);
    };

    document.addEventListener("mousedown", onDocumentPointerDown);
    document.addEventListener("touchstart", onDocumentPointerDown);

    return () => {
      document.removeEventListener("mousedown", onDocumentPointerDown);
      document.removeEventListener("touchstart", onDocumentPointerDown);
    };
  }, [isOpen]);

  const filteredOptions = useMemo(() => {
    if (!searchTerm) return options;
    return options.filter((option) =>
      option.label.toLowerCase().includes(searchTerm.toLowerCase())
    );
  }, [options, searchTerm]);

  const selectedOption = options.find((opt) => opt.value === value);

  return (
    <div ref={rootRef} style={{ position: "relative" }}>
      <label style={styles.label}>
        {label}
        {required && <span style={styles.required}>*</span>}
      </label>
      <div
        style={{
          ...styles.select,
          cursor: disabled ? "not-allowed" : "pointer",
          opacity: disabled ? 0.6 : 1,
        }}
        onClick={() => !disabled && setIsOpen(!isOpen)}
      >
        <span style={{ flex: 1, color: "#000" }}>{selectedOption?.label || placeholder}</span>
        {/* <span>▼</span> */}
      </div>
      {isOpen && !disabled && (
        <div
          style={{
            position: "absolute",
            top: "100%",
            left: 0,
            right: 0,
            backgroundColor: "white",
            border: "1px solid #d1d5db",
            borderRadius: "4px",
            maxHeight: "200px",
            overflowY: "auto",
            zIndex: 1000,
            boxShadow: "0 4px 6px rgba(0, 0, 0, 0.1)",
          }}
        >
          <input
            type="text"
            placeholder="Search..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            style={{
              width: "100%",
              padding: "8px",
              border: "none",
              borderBottom: "1px solid #e5e7eb",
              outline: "none",
            }}
            onClick={(e) => e.stopPropagation()}
          />
          <div>
            {filteredOptions.map((option) => (
              <div
                key={option.value}
                style={{
                  padding: "8px 12px",
                  cursor: "pointer",
                  backgroundColor: option.value === value ? "#f3f4f6" : "white",
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = "#f9fafb";
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor =
                    option.value === value ? "#f3f4f6" : "white";
                }}
                onClick={(e) => {
                  e.stopPropagation();
                  onChange(option.value, option.code);
                  setIsOpen(false);
                  setSearchTerm("");
                }}
              >
                {option.label}
              </div>
            ))}
            {filteredOptions.length === 0 && (
              <div style={{ padding: "8px 12px", color: "#9ca3af" }}>No results found</div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default LocationDropdown;

const styles = {
  overlay: {
    position: "fixed" as const,
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: "rgba(124, 104, 163, 0.8)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    zIndex: 1000,
    //   padding: "20px",
  },
  container: {
    background: "white",
    borderRadius: "16px",
    width: "90%",
    height: "90%",
    boxShadow: "0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)",
    display: "flex",
    flexDirection: "column" as const,
    overflow: "hidden",
  },
  header: {
    width: "100%",
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    flexDirection: "column" as const,
    padding: "20px 16px",
    borderBottom: "1px solid #e5e7eb",
    background: " rgba(245, 188, 52, 0.11)",
  },
  title: {
    fontSize: "14px",
    fontWeight: 700,
    color: "#000",
    margin: 0,
  },
  closeButton: {
    background: "none",
    border: "none",
    fontSize: "16px",
    color: "#6b7280",
    cursor: "pointer",
    padding: 0,
    //   width: "32px",
    //   height: "32px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    borderRadius: "4px",
    transition: "all 0.2s",
  },
  content: {
    //   padding: "24px 32px",
    padding: "20px 16px",
    flex: 1,
    overflow: "auto",
    //   backgroundColor: "red",
  },
  downloadLink: {
    color: "#f59e0b",
    fontSize: "14px",
    textDecoration: "none",
    fontWeight: 500,
    display: "block",
    textAlign: "right" as const,
    marginBottom: "5px",
  },
  uploadBox: {
    border: "1.5px dashed rgba(146, 148, 151, 1)",
    borderRadius: "12px",
    padding: "0px 20px",
    textAlign: "center" as const,
    cursor: "pointer",
    transition: "all 0.3s ease",
    backgroundColor: "#fafafa",
    //   marginBottom: "32px",
    display: "flex",
    alignItems: "center",
  },
  uploadBoxHover: {
    borderColor: "#f59e0b",
    backgroundColor: "#fffbf0",
  },
  uploadIcon: {
    fontSize: "32px",
    //   marginBottom: "12px",
    opacity: 0.5,
    paddingRight: "15px",
  },
  uploadText: {
    fontSize: "15px",
    color: "#6b7280",
    margin: 0,
  },
  uploadLink: {
    color: "#f59e0b",
    fontWeight: 500,
  },
  sectionTitle: {
    marginBottom: "20px",
    //   marginTop: "32px",
    fontSize: "14px",
    fontWeight: 700,
    color: "#000",
  },
  formGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(4, 1fr)",
    gap: "20px",
    //   marginBottom: "24px",
  },
  formGroup: {
    display: "flex",
    flexDirection: "column" as const,
  },
  label: {
    fontSize: "12px",
    fontWeight: 500,
    color: "#374151",
    marginBottom: "2px",
  },
  required: {
    color: "#ef4444",
    marginLeft: "2px",
  },
  input: {
    padding: "10px 14px",
    border: "1px solid #d1d5db",
    borderRadius: "16px",
    fontSize: "12px",
    color: "#111827",
    outline: "none",
    transition: "all 0.2s",
  },
  select: {
    padding: "5px 14px",
    border: "1px solid #d1d5db",
    borderRadius: "16px",
    fontSize: "12px",
    color: "#9ca3af",
    outline: "none",
    cursor: "pointer",
    backgroundColor: "white",
    appearance: "none" as const,
    backgroundImage:
      "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 12 12'%3E%3Cpath fill='%236b7280' d='M6 9L1 4h10z'/%3E%3C/svg%3E\")",
    backgroundRepeat: "no-repeat",
    backgroundPosition: "right 12px center",
    paddingRight: "36px",
  },
  footer: {
    display: "flex",
    justifyContent: "flex-end",
    gap: "12px",
    padding: "16px 32px",
    //   borderTop: "1px solid #e5e7eb",
    flexShrink: 0,
  },
  btnAddAnother: {
    padding: "8px 14px",
    fontSize: "12px",
    fontWeight: 500,
    borderRadius: "4px",
    cursor: "pointer",
    transition: "all 0.2s",
    backgroundColor: "rgba(227, 175, 50, 1)",
    color: "#000",
    border: "none",
    outline: "none",
  },
  btnSubmit: {
    padding: "4px 9px",
    fontSize: "12px",
    fontWeight: 500,
    borderRadius: "4px",
    border: "none",
    outline: "none",
    cursor: "pointer",
    transition: "all 0.2s",
    backgroundColor: "rgba(227, 175, 50, 1)",
    color: "#000",
  },
};
